import React, { useState, useEffect } from "react";
import styled from "styled-components";
import MainLayout from "../components/MainLayout";
import Button from "../components/Button";
import { useSupplyStore } from "../store/supplyStore";
import SupplyModal from "../components/SupplyModal";
import SupplyOrdersHistory from "../components/SupplyOrdersHistory";


const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-color: #081c21;

  @media (min-width: 768px) {
    gap: 24px;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 6px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 4px;
  width: 100%;
  max-width: 400px;

  @media (min-width: 768px) {
    gap: 8px;
    width: fit-content;
  }
`;

const Tab = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  background-color: ${(props) =>
    props.$isActive ? "#24B97D" : "transparent"};
  color: ${(props) => (props.$isActive ? "#FFFFFF" : "#8CA9A9")};
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  @media (min-width: 768px) {
    flex: none;
    padding: 12px 24px;
    font-size: 14px;
  }

  &:hover {
    color: ${(props) => (props.$isActive ? "#FFFFFF" : "#FFFFFF")};
    background-color: ${(props) =>
      props.$isActive ? "#24B97D" : "rgba(255, 255, 255, 0.05)"};
  }
`;

const ContentSection = styled.div<{ $isOrdersTab?: boolean }>`
  flex: 1;
  display: flex;
  align-items: ${props => props.$isOrdersTab ? 'flex-start' : 'center'};
  justify-content: center;
  padding: ${props => props.$isOrdersTab ? '0' : '20px 16px'};
  overflow-y: auto;

  @media (min-width: 768px) {
    padding: ${props => props.$isOrdersTab ? '0' : '40px 24px'};
  }
`;

const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  max-width: 600px;
  width: 100%;
  text-align: center;

  @media (min-width: 768px) {
    gap: 32px;
  }
`;

const SearchTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  line-height: 1.3;

  @media (min-width: 768px) {
    font-size: 32px;
    line-height: 1.2;
  }
`;

const SearchDescription = styled.p`
  font-size: 14px;
  color: #8ca9a9;
  margin: 0;
  line-height: 1.5;
  max-width: 500px;
  padding: 0 16px;

  @media (min-width: 768px) {
    font-size: 18px;
    padding: 0;
  }
`;

const SearchForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 0 16px;

  @media (min-width: 768px) {
    gap: 20px;
    padding: 0;
  }
`;

const SearchInputContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.05);
  border: 2px solid #609f94;
  border-radius: 12px;
  padding: 16px 20px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    border-radius: 16px;
    padding: 20px 24px;
  }

  &:focus-within {
    border-color: #24b97d;
    background-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 4px 30px rgba(36, 185, 125, 0.1);
  }
`;

const SearchInputField = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-size: 14px;
  font-family: "Ubuntu", sans-serif;

  &::placeholder {
    color: #8ca9a9;
  }

  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

const SearchIcon = styled.div`
  margin-right: 12px;
  color: #609f94;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }

  @media (min-width: 768px) {
    margin-right: 16px;
    
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const SearchButton = styled(Button)`
  font-size: 14px;
  padding: 16px 24px;
  border-radius: 12px;
  width: 100%;
  box-shadow: 0 4px 20px rgba(36, 185, 125, 0.2);

  @media (min-width: 768px) {
    font-size: 16px;
    padding: 16px 32px;
    width: auto;
    min-width: 160px;
  }

  &:hover:not(:disabled) {
    box-shadow: 0 6px 30px rgba(36, 185, 125, 0.3);
  }
`;

// Стили для результатов поиска
const SearchResults = styled.div`
  margin-top: 24px;
  width: 100%;
  max-width: 600px;
`;

const SearchResultCard = styled.div`
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const ProductImage = styled.div`
  width: 80px;
  height: 80px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const ProductInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProductName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  line-height: 1.3;
`;

const ProductMeta = styled.div`
  font-size: 14px;
  color: #8ca9a9;
`;

const ProductArticle = styled.div`
  display: inline-block;
  font-size: 12px;
  color: #24b97d;
  background-color: rgba(36, 185, 125, 0.1);
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid rgba(36, 185, 125, 0.2);
  font-weight: 500;
  width: fit-content;
`;

const ProductActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  
  @media (max-width: 768px) {
    width: 100%;
    align-items: center;
  }
`;

const SizeSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 400px;
`;

const SizeButton = styled.button<{ $selected: boolean }>`
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid ${props => props.$selected ? '#24b97d' : 'rgba(255, 255, 255, 0.2)'};
  background-color: ${props => props.$selected ? 'rgba(36, 185, 125, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.$selected ? '#24b97d' : '#ffffff'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 50px;

  &:hover {
    border-color: #24b97d;
    background-color: ${props => props.$selected ? 'rgba(36, 185, 125, 0.2)' : 'rgba(36, 185, 125, 0.05)'};
  }
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #609f94;
  background-color: transparent;
  color: #609f94;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: rgba(96, 159, 148, 0.1);
    color: #ffffff;
  }

  &:active:not(:disabled) {
    transform: scale(0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.span`
  min-width: 30px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
`;

const AddToCartSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const PriceDisplay = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #24b97d;
`;

// Стили для кнопки корзины
const CartButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #24b97d 0%, #1e9a66 100%);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(36, 185, 125, 0.3);
  transition: all 0.3s ease;
  z-index: 100;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 30px rgba(36, 185, 125, 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    width: 56px;
    height: 56px;
  }
`;

const CartBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #d85355;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  border: 2px solid #081c21;
`;

// Стили для множественного выбора размеров
const SizesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
  }
`;

const SizeCard = styled.div<{ $selected?: boolean; $available?: boolean }>`
  background-color: ${props => 
    props.$selected ? 'rgba(36, 185, 125, 0.1)' : 
    props.$available ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.02)'
  };
  border: 2px solid ${props => 
    props.$selected ? '#24b97d' : 
    props.$available ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)'
  };
  border-radius: 8px;
  padding: 12px 8px;
  text-align: center;
  cursor: ${props => props.$available ? 'pointer' : 'not-allowed'};
  opacity: ${props => props.$available ? 1 : 0.5};
  transition: all 0.2s ease;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (min-width: 768px) {
    padding: 12px;
    min-height: auto;
  }

  &:hover {
    ${props => props.$available && !props.$selected && `
      background-color: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.2);
    `}
  }
`;

const SizeLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 4px;
`;

const SizePrice = styled.div`
  font-size: 14px;
  color: #24b97d;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Supply: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"search" | "orders">("search");
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [searchTriggered, setSearchTriggered] = useState(false);
  
  // Состояние для выбранных размеров и количества каждого продукта (множественный выбор)
  const [productSelections, setProductSelections] = useState<Record<string, Record<string, number>>>({});
  
  const { 
    isSearching, 
    searchResults, 
    searchProduct,
    addToCart,
    cart,
    getCartItemsCount,
    getCartTotal
  } = useSupplyStore();

  // Отслеживаем изменения результатов поиска
  useEffect(() => {
    if (searchTriggered && !isSearching) {
      setSearchTriggered(false);
    }
  }, [searchResults, isSearching, searchTriggered]);

  // Состояние для отслеживания выполненного поиска
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    if (searchQuery.trim()) {
      setSearchTriggered(true);
      await searchProduct(searchQuery.trim());
      setHasSearched(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };



  // Функции для управления выбором размеров и количества (множественный выбор)
  const handleSizeSelect = (productId: string, size: string) => {
    setProductSelections(prev => {
      const productSizes = prev[productId] || {};
      const isSelected = (productSizes[size] || 0) > 0;
      return {
        ...prev,
        [productId]: {
          ...productSizes,
          [size]: isSelected ? 0 : 1
        }
      };
    });
  };

  const handleQuantityChange = (productId: string, size: string, delta: number) => {
    setProductSelections(prev => {
      const productSizes = prev[productId] || {};
      const currentQuantity = productSizes[size] || 0;
      const newQuantity = Math.max(0, Math.min(99, currentQuantity + delta));
      return {
        ...prev,
        [productId]: {
          ...productSizes,
          [size]: newQuantity
        }
      };
    });
  };

  const handleAddToCart = (product: any) => {
    const productSizes = productSelections[product.id] || {};
    let addedAny = false;
    
    // Добавляем все выбранные размеры в корзину
    Object.entries(productSizes).forEach(([size, quantity]) => {
      if (quantity > 0) {
        addToCart(product, size, quantity);
        addedAny = true;
      }
    });
    
    // Сброс выбора после добавления в корзину
    if (addedAny) {
      setProductSelections(prev => ({
        ...prev,
        [product.id]: {}
      }));
    }
  };

  const getTotalPrice = (product: any) => {
    const productSizes = productSelections[product.id] || {};
    let total = 0;
    
    Object.entries(productSizes).forEach(([size, quantity]) => {
      if (quantity > 0) {
        const sizeInfo = product.sizes.find((s: any) => s.size === size);
        if (sizeInfo) {
          total += sizeInfo.price * quantity;
        }
      }
    });
    
    return total;
  };

  const hasSelectedSizes = (productId: string) => {
    const productSizes = productSelections[productId] || {};
    return Object.values(productSizes).some(quantity => quantity > 0);
  };

  return (
    <MainLayout activePage="supply">
      <Container>
        <TopSection>
          <TabsContainer>
            <Tab
              $isActive={activeTab === "search"}
              onClick={() => setActiveTab("search")}
            >
              Поиск товаров
            </Tab>
            <Tab
              $isActive={activeTab === "orders"}
              onClick={() => setActiveTab("orders")}
            >
              История заказов
            </Tab>
          </TabsContainer>
        </TopSection>

        <ContentSection $isOrdersTab={activeTab === "orders"}>
          {activeTab === "search" && (
            <SearchSection>
              <SearchTitle>Создать закупку</SearchTitle>
              <SearchDescription>
                Найдите товары, добавьте их в корзину и оформите заказ
              </SearchDescription>
              
              <div style={{ marginBottom: '24px' }}>
                <Button
                  variant="primary"
                  onClick={() => setIsModalOpen(true)}
                  style={{ 
                    fontSize: '16px', 
                    padding: '16px 32px',
                    minWidth: '200px'
                  }}
                >
                  Начать закупку
                </Button>
              </div>

              {cart.length > 0 && (
                <div style={{ 
                  padding: '16px', 
                  background: 'rgba(36, 185, 125, 0.1)', 
                  borderRadius: '8px',
                  border: '1px solid rgba(36, 185, 125, 0.2)',
                  maxWidth: '400px',
                  margin: '0 auto'
                }}>
                  <p style={{ 
                    margin: '0 0 8px 0', 
                    color: '#24b97d', 
                    fontWeight: '500',
                    textAlign: 'center' 
                  }}>
                    В корзине: {getCartItemsCount()} товаров
                  </p>
                  <p style={{ 
                    margin: 0, 
                    color: '#8ca9a9', 
                    fontSize: '14px',
                    textAlign: 'center'
                  }}>
                    Общая сумма: {getCartTotal().toLocaleString()} ¥
                  </p>
                  <Button
                    variant="secondary"
                    onClick={() => setIsModalOpen(true)}
                    style={{ 
                      width: '100%',
                      marginTop: '12px',
                      fontSize: '14px'
                    }}
                  >
                    Продолжить закупку
                  </Button>
                </div>
              )}

            </SearchSection>
          )}

          {activeTab === "orders" && (
            <SupplyOrdersHistory />
          )}
        </ContentSection>

        {/* Модальное окно закупки */}
        <SupplyModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          mode="search"
        />
      </Container>
    </MainLayout>
  );
};

export default Supply;
